import { convertMajorToMinor, formatCurrencyAmount, getCurrencySymbol } from '@fox/finance/src/helpers';
import { InputBase } from '@mui/material';
import React, { forwardRef, useEffect, useState } from 'react';
import { MuiInputAdornment } from '../mui-text/index';
import { TextField } from '../text-field/index';
import { Typography } from '../typography';

type CompProps = React.ComponentProps<typeof TextField>;
type Sign = '+' | '-';

type CurrencyInputCommonProps = {
	amount?: CurrencyMinor;
	currency?: string;
	setAmount?: (a: CurrencyMinor | undefined) => void;
	positiveOnly?: boolean;
} & Pick<CompProps, 'sx' | 'onBlur' | 'size'>;

export type CurrencyInputProps = CurrencyInputCommonProps & {
	defaultValue?: CurrencyMinor;
	/** When true a +/− toggle is shown before the currency symbol. The sign is
	 *  inferred from whether `amount` is negative and managed internally — the
	 *  caller receives a signed CurrencyMinor via `setAmount` as usual. */
	allowSignToggle?: boolean;
	/** Called when the user explicitly toggles the sign (click, +, or − key).
	 *  Use this to track whether the user has overridden the default sign so
	 *  external resets (e.g. type changes) can respect their intent. */
	onSignToggled?: () => void;
} & Pick<CompProps, 'fullWidth' | 'label' | 'placeholder' | 'helperText' | 'status' | 'disabled' | 'required'>;

export type CurrencyInputRawProps = CurrencyInputCommonProps & Pick<CompProps, 'onClick' | 'autoFocus'>;

type CurrencyInputRawNewProps = Omit<CurrencyInputRawProps, 'setAmount'> & {
	majorValue?: boolean;
	setAmount?: (a: CurrencyMinor | CurrencyMajor | undefined) => void;
};

type GridCurrencyInputNewBaseProps = {
	allowFractionalCurrency?: boolean;
	currency?: string;
	style?: React.CSSProperties;
	positiveOnly?: boolean;
	labelOnDefault?: string;
};

type GridCurrencyInputMinorProps = GridCurrencyInputNewBaseProps & {
	majorValue?: false | undefined;
	amount: CurrencyMinor | undefined;
	setAmount: (amount: CurrencyMinor | undefined) => void;
};

type GridCurrencyInputMajorProps = GridCurrencyInputNewBaseProps & {
	majorValue: true;
	amount: CurrencyMajor | undefined;
	setAmount: (amount: CurrencyMajor | undefined) => void;
};

type GridCurrencyInputProps = GridCurrencyInputMinorProps | GridCurrencyInputMajorProps;

type GridCurrencyInputEditingNewProps = Omit<GridCurrencyInputProps, 'setAmount' | 'allowFractionalCurrency'> & {
	onChange: ((amount: CurrencyMajor | undefined) => void) | ((amount: CurrencyMinor | undefined) => void);
	onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	onClick?;
};

const absMinorToDisplay = (curMin: CurrencyMinor | undefined, currency: string): string => {
	if (curMin === undefined) return '';
	return formatCurrencyAmount(true, Math.abs(curMin) as CurrencyMinor, currency, false, false);
};

const minorToDisplay = (curMin: CurrencyMinor | undefined, currency: string): string => {
	if (curMin === undefined) return '';
	return formatCurrencyAmount(true, curMin, currency, false, false);
};

const parseToMinor = (str: string, currency: string, positiveOnly?: boolean): CurrencyMinor | null => {
	const cleaned = positiveOnly ? digitsOnly(str) : digitsAndNegativeOnly(str);
	if (cleaned.trim() === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;
	const major = Number(cleaned);
	if (Number.isNaN(major)) return null;
	const minor = convertMajorToMinor(major as CurrencyMajor, currency);
	if (minor === null) return null;
	// We are effectively clamping the value to a range of ±100 million dollars, which is ±10 billion cents.
	// This is a reasonable limit for most applications and prevents overflow issues.
	// TODO: Review this in the context of other currencies and their maximum values,
	// and consider making this configurable if needed.
	if (minor > 10_000_000_000) return 10_000_000_000 as CurrencyMinor;
	if (minor < -10_000_000_000) return -10_000_000_000 as CurrencyMinor;
	return minor;
};

/** Returns true for negative numbers and for negative zero (-0). */
const getSign = (n: CurrencyMinor | undefined): Sign => (n !== undefined && (n < 0 || Object.is(n, -0)) ? '-' : '+');

const CurrencyStartAdornment = ({ currency = 'AUD' }: { currency?: string }) => (
	<MuiInputAdornment sx={{ height: 'auto' }} position="start">
		<Typography fontWeight="bold">{getCurrencySymbol(currency)}</Typography>
	</MuiInputAdornment>
);

export const CurrencyInput: React.FC<CurrencyInputProps> = ({
	amount,
	setAmount,
	size,
	label,
	sx,
	defaultValue,
	placeholder,
	helperText,
	status,
	positiveOnly,
	allowSignToggle,
	onSignToggled,
	currency = 'AUD',
	...rest
}) => {
	const [editValue, setEditValue] = useState<string | null>(null);
	const sign = getSign(amount);

	const isEditing = editValue !== null;

	const applySign = (minor: CurrencyMinor, neg: boolean): CurrencyMinor => (neg ? -Math.abs(minor) : Math.abs(minor)) as CurrencyMinor;

	const toggleSign = () => {
		const newSign = sign === '+' ? '-' : '+';
		setAmount?.(applySign(amount ?? ZERO_CENTS, newSign === '-'));
		onSignToggled?.();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!allowSignToggle) return;
		if (e.key === '-') {
			e.preventDefault();
			if (sign === '+') toggleSign();
		} else if (e.key === '+') {
			e.preventDefault();
			if (sign === '-') toggleSign();
		}
	};

	const displayValue = allowSignToggle
		? isEditing
			? editValue
			: absMinorToDisplay(amount, currency)
		: isEditing
			? editValue
			: minorToDisplay(amount, currency);

	const signAdornment = allowSignToggle ? (
		<MuiInputAdornment
			sx={{ height: 'auto', cursor: 'pointer', userSelect: 'none' }}
			position="start"
			role="button"
			tabIndex={0}
			aria-label={sign === '+' ? 'Set negative amount' : 'Set positive amount'}
			onClick={toggleSign}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					toggleSign();
				}
				if (e.key === '+' && sign === '-') {
					e.preventDefault();
					toggleSign();
				}
				if (e.key === '-' && sign === '+') {
					e.preventDefault();
					toggleSign();
				}
			}}
		>
			<Typography fontWeight="bold" color={sign === '-' ? 'error' : 'textPrimary'}>
				{sign === '-' ? '−' : '+'}
			</Typography>
		</MuiInputAdornment>
	) : null;

	return (
		<TextField
			{...rest}
			label={label}
			fullWidth
			placeholder={placeholder}
			value={displayValue}
			inputProps={{ style: { textAlign: 'right' }, onKeyDown: handleKeyDown }}
			InputLabelProps={!defaultValue && !amount && !!placeholder ? { shrink: true } : {}}
			size={size ?? undefined}
			sx={{ ...sx, '& input::placeholder': { textAlign: 'left' } }}
			helperText={helperText}
			onFocus={() => setEditValue(allowSignToggle ? absMinorToDisplay(amount, currency) : minorToDisplay(amount, currency))}
			onChange={e => setEditValue(e.target.value)}
			onBlur={e => {
				if (allowSignToggle) {
					const minor = parseToMinor(digitsOnly(e.target.value), currency, true);
					// Always emit a signed value — signed zero when the field is empty/cleared,
					// so the parent never holds an ambiguous +0 that would fight the toggle state.
					setAmount?.(applySign(minor ?? ZERO_CENTS, sign === '-'));
				} else {
					const minor = parseToMinor(e.target.value, currency, positiveOnly);
					setAmount?.(minor ?? undefined);
				}
				setEditValue(null);
				rest.onBlur?.(e);
			}}
			status={status}
			startAdornment={
				<>
					{signAdornment}
					<CurrencyStartAdornment currency={currency} />
				</>
			}
		/>
	);
};

export const CurrencyInputRaw = forwardRef<any, CurrencyInputRawNewProps>(
	({ amount, setAmount, currency, positiveOnly, sx, onBlur, size, onClick, autoFocus, majorValue }, ref) => {
		const [editValue, setEditValue] = useState<string | null>(null);
		const displayValue = amount !== undefined ? (majorValue ? String(amount) : minorToDisplay(amount, currency ?? 'AUD')) : '';
		return (
			<InputBase
				inputRef={ref}
				value={editValue !== null ? editValue : displayValue}
				inputProps={{ style: { textAlign: 'right' } }}
				size={size ?? undefined}
				sx={{ padding: '0.5em', ...sx }}
				onChange={e => setEditValue(e.target.value)}
				onClick={onClick}
				autoFocus={autoFocus}
				onBlur={e => {
					if (majorValue) {
						const raw = e.target.value.trim();
						const cleaned = positiveOnly ? digitsOnly(raw) : digitsAndNegativeOnly(raw);
						if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') {
							setAmount?.(undefined);
						} else {
							const parsed = Number(cleaned);
							const next = Number.isNaN(parsed) || (positiveOnly && parsed < 0) ? undefined : (parsed as CurrencyMajor);
							setAmount?.(next);
						}
					} else {
						const minor = parseToMinor(e.target.value, currency ?? 'AUD', positiveOnly);
						setAmount?.(minor ?? undefined);
					}
					setEditValue(null);
					onBlur?.(e);
				}}
				startAdornment={
					<>
						<CurrencyStartAdornment currency={currency} />
					</>
				}
			/>
		);
	}
);

export function digitsOnly(str: string) {
	return str.replace(/[^\d.]+/g, '');
}
export function digitsAndNegativeOnly(str: string) {
	return str.replace(/[^-\d.]+/g, '');
}

declare const __brand: unique symbol;
type Brand<B> = { [__brand]: B };
export type Branded<T, B> = T & Brand<B>;

export type CurrencyMinor = Branded<number, 'currency-minor'>;
export type CurrencyMajor = Branded<number, 'currency-major'>;
export type Cents = CurrencyMinor;
export type Dollars = CurrencyMajor;

//! TODO I am aware this duplicates some of the base currency code but for now this is our primary need - we should move all of this to @fox/financial soon
// TODO this should be explicit currency stuff
export function toCents(amountDollars: string | Dollars) {
	return Math.round(Number(typeof amountDollars === 'string' ? digitsAndNegativeOnly(amountDollars) : amountDollars) * 100) as CurrencyMinor;
}
export function toDollars(amountCents: Cents | number) {
	return Math.round(Number(amountCents) / 100) as Dollars;
}

/** A special branded value Cents for the zero amount */
export const ZERO_CENTS = 0 as Cents;

/** A special branded value for Dollars for the zero amount */
export const ZERO_DOLLARS = 0 as Dollars;

export function multiplyBranded<T extends number>(value: T, multiplier: number): T {
	return (value * multiplier) as T;
}
export class BrandMath {
	static round<T extends number>(n: T): T {
		return Math.round(n) as T;
	}
	static add<T extends number>(a: T, b: number): T {
		return (a + b) as T;
	}
	static sum<T extends number>(...a: T[]): T {
		return a.reduce((p, c) => this.add(p, c), 0 as T);
	}
	static subtract<T extends number>(a: T, b: T): T {
		return (a - b) as T;
	}
	static multiply<T extends number>(num: T, mul: number): T {
		return (num * mul) as T;
	}
	static max<T extends number>(...values: T[]): T {
		return Math.max(...values) as T;
	}
}

export function toCurrency(amountCents?: Cents) {
	if (amountCents === undefined) return '';

	return String(amountCents / 100);
}

export const GridCurrencyInput = ({
	amount: val,
	allowFractionalCurrency,
	currency,
	setAmount,
	style,
	positiveOnly,
	majorValue,
	labelOnDefault
}: GridCurrencyInputProps) => {
	const [mode, setMode] = useState(false);
	const [hover, setHover] = useState(false);

	useEffect(() => {
		if (!hover) return;

		const cancel = setTimeout(() => {
			setMode(true);
		}, 1000);
		return () => clearTimeout(cancel);
	}, [hover]);

	if (mode) {
		return (
			<GridCurrencyInputEditing
				positiveOnly={positiveOnly}
				ref={r => {
					if (r) {
						r.focus();
					}
				}}
				onBlur={() => setMode(false)}
				amount={val}
				style={style}
				onChange={setAmount}
				currency={currency}
				majorValue={majorValue}
			/>
		);
	}

	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				padding: '0.5em',
				...style
			}}
			onClick={() => requestAnimationFrame(() => setMode(true))}
			onMouseOver={() => setHover(true)}
			onMouseOut={() => setHover(false)}
		>
			{val === undefined || val === null ? (
				<Typography color="textSecondary" fontStyle="italic" paddingRight="0.5em">
					{labelOnDefault ?? ''}
				</Typography>
			) : (
				<>
					<CurrencyStartAdornment currency={currency} />
					<span style={{ flex: 1, textAlign: 'right' }}>
						{majorValue
							? `${formatCurrencyAmount(false, val ?? null, currency ?? 'AUD', false, allowFractionalCurrency ?? false)}`
							: `${formatCurrencyAmount(true, val ?? null, currency ?? 'AUD', false, allowFractionalCurrency ?? false)}`}
					</span>
				</>
			)}
		</div>
	);
};

export const GridCurrencyInputEditing = forwardRef<any, GridCurrencyInputEditingNewProps>(
	({ amount: val, onChange, onBlur, style, onClick, currency, positiveOnly, majorValue }, ref) => {
		//TODO: Future work, get the currency in here (move to @fox/financial)
		return (
			<CurrencyInputRaw
				positiveOnly={positiveOnly}
				currency={currency}
				ref={ref}
				amount={val as CurrencyMinor}
				onClick={onClick}
				onBlur={onBlur}
				sx={{ paddingLeft: '0.5rem', ...style }}
				setAmount={e => onChange(e as any)}
				majorValue={majorValue}
			/>
		);
	}
);
